import {
  CountyCodeShape,
  Div,
  Dropdown,
  Flex,
  Gutter,
  Input,
  OptionListOption,
  PhoneNumberInput,
  Title,
} from '@gaiads/telia-react-component-library';
import { COUNTRIES } from '@cdaas/reusable-components-daas';
import { useTranslation } from 'react-i18next';
import { Address, Person } from '@cdaas/api-clients/dist/asset-admin-openapi';

import { COUNTRY_OPTIONS } from '../../../utils';
import { BillingAddressErrors, BillingContactPersonErrors } from '../utils';

import styles from './BillingInformation.module.scss';

// Draft versions (all fields are optional) for billing address and contact person
export type BillingAddressDraft = Partial<Address>;
export type BillingContactPersonDraft = Partial<Person>;

interface BillingformationProps {
  billingAddress: BillingAddressDraft | undefined;
  setBillingAddress: (billingAddress: BillingAddressDraft) => void;
  billingContactPerson: BillingContactPersonDraft | undefined;
  setBillingContactPerson: (billingContactPerson: BillingContactPersonDraft) => void;
  addressErrors: BillingAddressErrors | undefined;
  contactPersonErrors: BillingContactPersonErrors | undefined;
}

export default function BillingInformation(props: BillingformationProps) {
  const { billingAddress, setBillingAddress, billingContactPerson, setBillingContactPerson, addressErrors, contactPersonErrors } = props;

  const { t } = useTranslation('organizationForm');

  const countryCallingCodeOptions: CountyCodeShape[] = COUNTRIES.map(cc => ({
    ...cc,
    countryName: t(`countries.${cc.countryName}`),
  }));
  const selectedCountryCallingCodeOption: CountyCodeShape | undefined = countryCallingCodeOptions.find(cc => {
    return cc.code === billingContactPerson?.country_calling_code;
  });

  const countryOptions: OptionListOption[] = COUNTRY_OPTIONS.map(country => ({
    label: t(`countries.${country.countryName}`),
    value: country.value,
    disabled: !country.available,
  }));
  const selectedCountryOption = countryOptions.find(country => {
    return country.value === billingAddress?.country;
  });

  return (
    <Div padding={{ size: 'lg' }} boxShadow="md">
      <Title size={'sm'}>{t('billing_information.billing_information')}</Title>
      <Flex directionColumn className={styles['customer-organization-form__billing-info__billing-info-inputs-container']}>
        <Gutter size="layout-5" />

        <Title size={'xs'}>{t('billing_information.billing_address')}</Title>

        <Gutter size="layout-5" />

        <Input
          id="billing-street"
          label={t('billing_information.street_input_label')}
          onChange={(_event, value) =>
            setBillingAddress({
              ...billingAddress,
              street: value,
            })
          }
          value={billingAddress?.street || ''}
          required={true}
          errorText={addressErrors?.street}
        />
        <Gutter size="layout-5" />
        <Input
          id="billing-address1"
          label={t('billing_information.address1_input_label')}
          onChange={(_event, value) =>
            setBillingAddress({
              ...billingAddress,
              address1: value,
            })
          }
          value={billingAddress?.address1 || ''}
        />
        <Gutter size="layout-5" />
        <Input
          id="billing-address2"
          label={t('billing_information.address2_input_label')}
          onChange={(_event, value) =>
            setBillingAddress({
              ...billingAddress,
              address2: value,
            })
          }
          value={billingAddress?.address2 || ''}
        />
        <Gutter size="layout-5" />
        <Input
          id="billing-city"
          label={t('billing_information.city_input_label')}
          onChange={(_event, value) =>
            setBillingAddress({
              ...billingAddress,
              city: value,
            })
          }
          value={billingAddress?.city || ''}
          required={true}
          errorText={addressErrors?.city}
        />
        <Gutter size="layout-5" />
        <Input
          id="billing-zip"
          label={t('billing_information.zip_input_label')}
          onChange={(_event, value) =>
            setBillingAddress({
              ...billingAddress,
              zip: value,
            })
          }
          value={billingAddress?.zip || ''}
          required={true}
          errorText={addressErrors?.zip}
        />
        <Gutter size="layout-5" />
        <Dropdown
          required
          label={t('billing_information.country_input_dropdown_label')}
          name="billing-address-country-dropdown"
          options={countryOptions}
          selectedOption={selectedCountryOption}
          onChange={(event, option) => {
            setBillingAddress({
              ...billingAddress,
              country: option?.value,
            });
          }}
          errorText={addressErrors?.country}
        />

        <Gutter size="layout-5" />

        <Title size={'xs'}>{t('billing_information.billing_contact_person')}</Title>

        <Gutter size="layout-5" />

        <Input
          id="billing-first-name"
          label={t('billing_information.first_name_input_label')}
          onChange={(_event, value) =>
            setBillingContactPerson({
              ...billingContactPerson,
              first_name: value,
            })
          }
          value={billingContactPerson?.first_name || ''}
          required={true}
          errorText={contactPersonErrors?.first_name}
        />
        <Gutter size="layout-5" />
        <Input
          id="billing-last-name"
          label={t('billing_information.last_name_input_label')}
          onChange={(_event, value) =>
            setBillingContactPerson({
              ...billingContactPerson,
              last_name: value,
            })
          }
          value={billingContactPerson?.last_name || ''}
          required={true}
          errorText={contactPersonErrors?.last_name}
        />
        <Gutter size="layout-5" />
        <Input
          id="billing-email"
          label={t('billing_information.email_input_label')}
          onChange={(_event, value) =>
            setBillingContactPerson({
              ...billingContactPerson,
              email: value,
            })
          }
          value={billingContactPerson?.email || ''}
          type="email"
          required={true}
          errorText={contactPersonErrors?.email}
        />
        <Gutter size="layout-5" />
        <Div data-testid="billingContactPersonPhoneNumberInputContainer">
          <PhoneNumberInput
            required
            label={t('billing_information.phone_number_input_label')}
            onNumberChange={(event, value) => {
              setBillingContactPerson({
                ...billingContactPerson,
                phone_number: value,
              });
            }}
            numberValue={billingContactPerson?.phone_number}
            countryCodes={countryCallingCodeOptions}
            selectedCountryCode={selectedCountryCallingCodeOption}
            onCountryCodeChange={(event, option: CountyCodeShape | undefined) => {
              setBillingContactPerson({
                ...billingContactPerson,
                country_calling_code: option?.code,
              });
            }}
            errorText={contactPersonErrors?.country_calling_code ?? contactPersonErrors?.phone_number}
          />
        </Div>
      </Flex>
    </Div>
  );
}
